import { type Dispatch, type SetStateAction, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import {
  ResourceRequest,
  ResourceRequestItem,
  useUpdateResourceRequest,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import dayjs from 'dayjs';
import { use213rrStatusLabel } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrVersioning';
import { Ics213RrExpandedPanel } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrExpandedPanel';
import { itemDescription } from 'components/IAP/ResourceRequests/ics213rr/ics213rrPriority';
import { CustomResourcesDropdown } from './CustomResourcesDropdown';
import { NULL_VALUE } from 'constants/displayConstants';
import { REQUEST_GRID_CLASS } from './RequestHeaderCard';
import { Badge } from '@/components/ui/badge';

type RequestCardProps = {
  request: ResourceRequest;
  memberPositions: { positionId: string; positionName: string }[];
  reqCount: Record<string, number>;
  /** Incident workspace this request was created in. Hub list only. */
  workspaceName?: string;
  /** Hub list is view-only. Edit and delete use the current incident id. */
  readOnly?: boolean;
  /** Open this card when a notification deeplink names it. */
  defaultExpanded?: boolean;
};

export const RequestCard = ({
  request,
  memberPositions,
  reqCount,
  workspaceName,
  readOnly = false,
  defaultExpanded = false,
}: RequestCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const statusLabel = use213rrStatusLabel(
    request.resource_request_id,
    request.status
  );
  const { mutate: updateResourceRequest } = useUpdateResourceRequest();

  const handleDelete = (resourceRequest: ResourceRequest) => {
    updateResourceRequest({
      requestId: resourceRequest.resource_request_id,
      data: { ...resourceRequest, archived: true },
    });
  };

  return (
    <Card className="tw:bg-card tw:text-card-foreground">
      <div
        role="button"
        tabIndex={0}
        className={`${REQUEST_GRID_CLASS} tw:w-full tw:px-3 tw:py-2 tw:text-left tw:text-sm tw:text-foreground`}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        <div>
          {expanded ? (
            <ChevronDown className="tw:size-4 tw:text-muted-foreground" />
          ) : (
            <ChevronRight className="tw:size-4 tw:text-muted-foreground" />
          )}
        </div>
        <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-2">
          <span className="tw:min-w-0">{request.name}</span>
          {workspaceName ? (
            <Badge variant="outline">{workspaceName}</Badge>
          ) : null}
        </div>
        <div>{request.requestor_name}</div>
        <div className="tw:text-muted-foreground">{statusLabel}</div>
        <div className="tw:text-muted-foreground">
          {request.items?.map((item, idx) => (
            <div key={`${item.id ?? item.name}-${idx}`}>
              <div>
                Kind:{item.kind}, Type: {item.type}, Priority:{' '}
                {item.priority || NULL_VALUE}
              </div>
              <div>Description: {itemDescription(item)}</div>
              {request.items && idx < request.items.length - 1 ? (
                <hr className="tw:my-1 tw:border-border" />
              ) : null}
            </div>
          ))}
        </div>
        <div>
          {readOnly ? null : (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Delete request"
              onClick={(e) => {
                handleDelete(request);
                e.stopPropagation();
              }}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      </div>
      {expanded ? (
        <CardContent className="tw:border-t tw:border-border tw:p-0">
          <Ics213RrExpandedPanel
            request={request}
            memberPositions={memberPositions}
            reqCount={reqCount}
            readOnly={readOnly}
          />
        </CardContent>
      ) : null}
    </Card>
  );
};

export const Resource = ({
  resource,
  deletable,
  onEdit,
  onDelete,
  arrIndex,
  showLogisticsStep = false,
  logisticsItems = [],
  setLogisticsItems,
  reqCount,
  memberPositions,
  requestCreatorUserGuid,
  requestorPosition,
}: {
  resource: ResourceRequestItem;
  deletable?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  arrIndex?: number;
  showLogisticsStep?: boolean;
  logisticsItems?: ResourceRequestItem[];
  setLogisticsItems?: Dispatch<SetStateAction<ResourceRequestItem[][]>>;
  reqCount?: Record<string, number>;
  memberPositions?: { positionId: string; positionName: string }[];
  requestCreatorUserGuid?: string | null;
  requestorPosition?: string | null;
}) => {
  return (
    <div className="tw:mb-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3">
      <div className="tw:grid tw:grid-cols-2 tw:gap-3 tw:text-sm tw:lg:grid-cols-9">
        <div>
          <div className="tw:text-muted-foreground">Quantity</div>
          <div>{resource.quantity || NULL_VALUE}</div>
        </div>
        <div>
          <div className="tw:text-muted-foreground">Kind</div>
          <div>{resource.kind || NULL_VALUE}</div>
        </div>
        <div>
          <div className="tw:text-muted-foreground">Type</div>
          <div>{resource.type || NULL_VALUE}</div>
        </div>
        <div>
          <div className="tw:text-muted-foreground">Priority</div>
          <div>{resource.priority || NULL_VALUE}</div>
        </div>
        <div className="tw:lg:col-span-2">
          <div className="tw:text-muted-foreground">Description</div>
          <div>{itemDescription(resource) || NULL_VALUE}</div>
        </div>
        <div>
          <div className="tw:text-muted-foreground">
            Requested Reporting Location
          </div>
          <div>{resource.requested_reporting_location || NULL_VALUE}</div>
        </div>
        <div>
          <div className="tw:text-muted-foreground">Requested Report Date</div>
          <div>
            {resource.requested_reporting_datetime
              ? dayjs(resource.requested_reporting_datetime).format(
                  'YYYY-MM-DD HH:mm'
                )
              : NULL_VALUE}
          </div>
        </div>
        <div>
          <div className="tw:text-muted-foreground">Requested Demob Date</div>
          <div>
            {resource.requested_demob_datetime
              ? dayjs(resource.requested_demob_datetime).format(
                  'YYYY-MM-DD HH:mm'
                )
              : NULL_VALUE}
          </div>
        </div>
      </div>
      {onEdit || deletable ? (
        <div className="tw:mt-2 tw:flex tw:flex-wrap tw:gap-2">
          {onEdit ? (
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label="Edit resource"
              title="Edit resource"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <Pencil />
            </Button>
          ) : null}
          {deletable ? (
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label="Delete resource"
              title="Delete resource"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
            >
              <Trash2 />
            </Button>
          ) : null}
        </div>
      ) : null}
      {showLogisticsStep && setLogisticsItems ? (
        <div className="tw:mt-3">
          <div className="tw:mb-1 tw:text-sm tw:text-muted-foreground">
            Attach Resources
          </div>
          <CustomResourcesDropdown
            arrIndex={arrIndex}
            logisticsItems={logisticsItems}
            setLogisticsItems={setLogisticsItems}
            reqCount={reqCount}
            isDisabled={
              !memberPositions?.some(
                (pos) => pos.positionName === 'Logistics Section Chief'
              )
            }
            requestCreatorUserGuid={requestCreatorUserGuid}
            requestorPosition={requestorPosition}
          />
        </div>
      ) : null}
    </div>
  );
};
